import { FC } from 'react';

import clsx from 'clsx';
import { useFormik } from 'formik';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import { ModalForm } from '../form';

import css from './CreateModalForm.module.css';

import { modalSettingsService } from 'v2/api/modalSettingsService';
import { Card } from 'v2/components/card/Card';
import { CardHeader } from 'v2/components/card/cardHeader/CardHeader';
import { ButtonsFormControl } from 'v2/components/form/buttonsFormControl/ButtonsFormControl';
import { RichTextarea } from 'v2/components/richTextarea/RichTextarea';
import { handleHttpError } from 'v2/utils';

interface CreateModalFormProps {
  onSuccess: VoidFunction;
}

export const CreateModalForm: FC<CreateModalFormProps> = ({ onSuccess }) => {
  const { trigger, isMutating } = useSWRMutation('createModal', modalSettingsService.createModal, {
    onSuccess: () => {
      onSuccess();
      toast.success('The modal has been successfully created');
      formik.resetForm();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<ModalForm>({
    initialValues: { header: '', metricParam: '', buttons: [], body: '' },
    onSubmit: (values) => {
      trigger(values);
    },
  });

  const handleChangeRichTextarea = (value: string) => {
    formik.setFieldValue('body', value);
  };

  return (
    <Card className={css.createModalFormContainer}>
      <CardHeader title="Create Modal" icon={Plus} />

      <form className={clsx('form with-top-space', css.createModalForm)} onSubmit={formik.handleSubmit}>
        <TextField
          name="header"
          value={formik.values.header}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="Header"
          required
        />

        <RichTextarea value={formik.values.body} label="Description" onChange={handleChangeRichTextarea} />

        <TextField
          name="metricParam"
          value={formik.values.metricParam}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="Metric Id"
          required
        />

        <ButtonsFormControl formik={formik} />

        <Button type="submit" loading={isMutating} variant="contained" size="large">
          Create
        </Button>
      </form>
    </Card>
  );
};
